import React, { useState, useEffect } from "react";
import { saveCard } from "./services/supabaseActions";
import SportsCardForm from "./components/forms/SportsCardForm";
import TCGForm from "./components/forms/TCGForm";
import LegoForm from "./components/forms/LegoForm";
import DecorForm from "./components/forms/DecorForm";
import FunkoForm from "./components/forms/FunkoForm";
import FashionForm from "./components/forms/FashionForm";
import MemorabiliaForm from "./components/forms/MemorabiliaForm";
import MusicForm from "./components/forms/MusicForm";
import MiscForm from "./components/forms/MiscForm";
import "./CardForm.css";

const formMap = {
  "Sports Card": SportsCardForm,
  "TCG": TCGForm,
  "Legos/Building Blocks": LegoForm,
  "Home Decor": DecorForm,
  "Funko": FunkoForm,
  "Fashion & Apparel": FashionForm,
  "Memorabilia": MemorabiliaForm,
  "Music": MusicForm,
  "Misc": MiscForm,
};

export default function CardForm({
  cardData,
  initialCategory,
  user,
  onAddItem,
  onCancel,
  onSubmitting = null,
  onSubmitError = null,
  categories = [],
  subcategories = [],
  isEdit = false,
}) {
  const initialState = {
    category_id: initialCategory || "",
    subcategory_id: "",
    year: "",
    year_end: "",
    brand: "",
    set_name: "",
    subset: "",
    suggested_name: "",
    card_number: "",
    autographed: false,
    autograph_grade: "",
    graded: false,
    grade: "",
    grading_company: "",
    cert_number: "",
    serial_number: "",
    notes: "",
    price_paid: "",
    estimated_value: "",
    variations: [],
    for_sale: false,
    is_private: false,
  };

  const [card, setCard] = useState({
    ...initialState,
    ...cardData,
    autographed:
      cardData?.autographed ??
      cardData?.attributes?.autographed ??
      initialState.autographed,
    graded:
      cardData?.graded ??
      cardData?.attributes?.graded ??
      initialState.graded,
    grade:
      cardData?.grade ??
      cardData?.attributes?.grade ??
      initialState.grade,
    grading_company:
      cardData?.grading_company ??
      cardData?.attributes?.grading_company ??
      initialState.grading_company,
    cert_number:
      cardData?.cert_number ??
      cardData?.attributes?.cert_number ??
      initialState.cert_number,
    autograph_grade:
      cardData?.autograph_grade ??
      cardData?.attributes?.autograph_grade ??
      initialState.autograph_grade,
    serial_number:
      cardData?.serial_number ??
      cardData?.attributes?.serial_number ??
      initialState.serial_number,
    category_id: cardData?.category_id || initialCategory || "",
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const FormComponent =
    formMap[
      categories.find(
        (category) => String(category.id) === String(card.category_id)
      )?.name
    ] || SportsCardForm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFormError("");
    if (!user?.id) {
      setFormError("You must be signed in to save an item.");
      return;
    }

    const startYear = card.year ? Number(card.year) : null;
    const endYear = card.year_end ? Number(card.year_end) : null;
    if (endYear !== null && (startYear === null || endYear < startYear)) {
      setFormError("End year must be the same as or later than the start year.");
      return;
    }

    const rawSerialNumber = String(card.serial_number ?? "").trim();
    let parsedSerialNumber = null;
    let enteredSerialTotal = null;

    if (rawSerialNumber) {
      const serialMatch = rawSerialNumber.match(/^(\d+)(?:\s*\/\s*(\d+))?$/);
      if (!serialMatch) {
        setFormError("Serial number should be the copy number (for example 3) or the full numbering (for example 3/5).");
        return;
      }

      parsedSerialNumber = Number(serialMatch[1]);
      enteredSerialTotal = serialMatch[2] ? Number(serialMatch[2]) : null;

      if (parsedSerialNumber < 1 || (enteredSerialTotal !== null && enteredSerialTotal < 1)) {
        setFormError("Serial number must be greater than zero.");
        return;
      }

      if (enteredSerialTotal !== null && parsedSerialNumber > enteredSerialTotal) {
        setFormError("The serial number cannot be greater than the total print numbering.");
        return;
      }

      const selectedTotals = (Array.isArray(card.variations) ? card.variations : [])
        .map((variation) => String(variation?.name || variation || "").trim().match(/\/\s*(\d+)\s*$/))
        .filter(Boolean)
        .map((match) => Number(match[1]));

      if (enteredSerialTotal !== null && selectedTotals.length > 0 && !selectedTotals.includes(enteredSerialTotal)) {
        setFormError(`The entered serial total /${enteredSerialTotal} does not match the selected numbered variant.`);
        return;
      }
    }

    setIsSubmitting(true);

    let tempId = null;

    try {
      const attributes = {
        // Card number remains here for provisional/unlinked items so it can be
        // reviewed and searched. The RPC removes it once identity is canonical.
        card_number: card.card_number || null,
        // Preserve proposed subset for unlinked/pending items until canonical approval.
        subset: card.subset || null,
        autographed: Boolean(card.autographed),
        autograph_grade:
          card.autographed && card.graded ? card.autograph_grade || null : null,
        graded: Boolean(card.graded),
        grade: card.graded ? card.grade || null : null,
        grading_company: card.graded ? card.grading_company || null : null,
        cert_number: card.graded ? card.cert_number || null : null,
        // Copy-specific numerator only. The denominator belongs to the canonical
        // numbered variant (for example, Red /5).
        serial_number: parsedSerialNumber,
      };

      const payload = {
        user_id: user?.id,
        name: card.suggested_name || card.name || "",
        brand: card.brand || "",
        set_name: card.set_name || "",
        subset: card.subset || null,
        year: card.year || null,
        year_end: card.year_end || null,
        card_number: card.card_number || null,
        condition: card.condition || null,
        price_paid: card.price_paid || null,
        estimated_value: card.estimated_value || null,
        description: card.notes || null,
        for_sale: card.for_sale ?? false,
        is_private: card.is_private ?? false,
        attributes,
        category_id: card.category_id || null,
        subcategory_id: card.subcategory_id || null,
        variations: (Array.isArray(card.variations) ? card.variations : [])
          .map((variation) => {
            if (typeof variation === "string") {
              // Legacy string values are not trusted as canonical.
              return {
                id: null,
                name: variation.trim(),
                canonical: false,
              };
            }

            if (!variation?.name) return null;

            return {
              id: variation.id ? Number(variation.id) : null,
              suggestion_id: variation.suggestion_id
                ? Number(variation.suggestion_id)
                : null,
              name: String(variation.name).trim(),
              canonical: Boolean(variation.canonical && variation.id),
            };
          })
          .filter((variation) => variation?.name),
        images: imageFiles,
        card_id: card.id || null,
      };

      // Optimistic UI
      try {
        tempId = Date.now();
        const optimisticCard = {
          id: tempId,
          ...card,
          name: card.suggested_name || card.name || "",
          set_name: card.set_name,
          year: Number(card.year || 0),
          year_end: card.year_end ? Number(card.year_end) : null,
          _optimistic: true,
          image_url: cardData?.image_url || previews[0] || null,
        };
        onSubmitting?.(optimisticCard);
      } catch {}

      const savedCard = await saveCard(payload, user.id, isEdit, card.id);
      const returnedRow = Array.isArray(savedCard) ? savedCard[0] : savedCard;

      if (!returnedRow?.id && returnedRow?.user_item_id) {
        returnedRow.id = returnedRow.user_item_id;
      }

      onAddItem?.(returnedRow);

      if (!isEdit) {
        setCard(initialState);
        setImageFiles([]);
        setPreviews([]);
      }

      onCancel?.();
    } catch (err) {
      console.error(isEdit ? "Edit card failed:" : "Add card failed:", err);
      if (tempId) onSubmitError?.(tempId);
      setFormError(
        `Failed to ${isEdit ? "edit" : "add"} card: ${err.message || JSON.stringify(err)}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const hobbliVisionImage = cardData?.hobbliVision?.imageFile;

    if (!hobbliVisionImage) {
      return;
    }

    setImageFiles((prev) => {
      // HobbliVision should only seed the image list.
      // Never overwrite images the user has already added.
      if (prev.length > 0) {
        return prev;
      }

      return [hobbliVisionImage];
    });

    setPreviews((prev) => {
      // Same rule for previews.
      if (prev.length > 0) {
        return prev;
      }

      return [URL.createObjectURL(hobbliVisionImage)];
    });
  }, [cardData?.hobbliVision?.imageFile]);

  return (
    <div className="card-form-container">
        {formError && (
          <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(180,35,24,.10)", color: "#b42318", fontSize: ".86rem" }}>
            {formError}
          </div>
        )}
      <FormComponent
        card={card}
        setCard={setCard}
        imageFiles={imageFiles}
        setImageFiles={setImageFiles}
        previews={previews}
        setPreviews={setPreviews}
        existingImages={
          Array.isArray(cardData?.images)
            ? cardData.images
            : cardData?.image_url
              ? [{ image_url: cardData.image_url, position: 0 }]
              : []
        }
        categories={categories}
        subcategories={subcategories}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isEditing={isEdit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
